import {
    Autocomplete,
    Button,
    Divider,
    TextField
} from '@mui/material';
import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { WorkLogItem } from '../work-log-item';
import { TimeTrackComment, TimeTrackPayload } from '../../models/report/report-timetrack';
import { isValidEmail } from '../../shared/utils';
const STRIP_LEADING_ZEROS = /\b0+/g;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => createStyles({
    root: {
        display: 'flex',
        flexDirection: 'column'
    },
    textField: {
        flex: 1,
        minHeight: '6em'
    },
    textFieldBlank: {},
    buttonsContainer: {
        '& > *': {
            marginRight: '1.5em'
        }
    },
    commentItemContainer: {
        paddingBottom: '1em'
    },
    timeTrackerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        columnGap: '2em',
        '& > div': {
            width: '45%'
        },
    }
});

const DEFAULT_VALUES = {
    email: '',
    minutesValue: 0,
    hoursValue: 0,
    isEmailInvalid: false
};

interface Props extends WithStyles<typeof styles> {
    comments: TimeTrackComment[] | undefined;
    isLoading?: boolean;
    emailList: string[];
    updateEmailList: (email: string) => void;
    onTimeTrackPost: (comment: TimeTrackPayload) => void;
}

interface States {
    email: string;
    minutesValue: number;
    hoursValue: number;
    isEmailInvalid: boolean;
}

class WorkLogEditor extends React.Component<Props, States> {
    public static defaultProps: Partial<Props> = {
    };

    state = DEFAULT_VALUES;
    
    isComponentMounted = true;

    componentWillUnmount() {
        this.isComponentMounted = false;
    }

    onEmailChanged = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        const { updateEmailList } = this.props;
        const email = value.trim();
        const isEmailValid = isValidEmail(email);

        this.setState({
            email,
            isEmailInvalid: !isEmailValid
        }, () => {
            updateEmailList(email);
        });
    };

    onPostClicked = () => {
        const { onTimeTrackPost } = this.props;
        const { email, hoursValue, minutesValue } = this.state;

        onTimeTrackPost({email, hours: hoursValue, minutes: minutesValue});
        this.setState(DEFAULT_VALUES);
    };

    onCancelClicked = () => {
        this.setState(DEFAULT_VALUES);
    };

    onMinutesChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const currentValue = event.target.value.replace(STRIP_LEADING_ZEROS, '');
        const minutesValue = currentValue === '' ? 0 : Number.parseInt(currentValue, 10);

        if (minutesValue <= 999) {
            this.setState({minutesValue});
        }
    };

    onHoursChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const currentValue = event.target.value.replace(STRIP_LEADING_ZEROS, '');
        const hoursValue = currentValue === '' ? 0 : Number.parseInt(currentValue, 10);

        if (hoursValue <= 999) {
            this.setState({hoursValue});
        }
    };

    public render() {
        const {
            classes,
            comments,
            isLoading,
            emailList
        } = this.props;
        const{ 
            email,
            minutesValue,
            hoursValue,
            isEmailInvalid
        } = this.state;
        const commentsExist = !!comments && comments.length > 0;
        const commentsNumber = comments ? comments.length -1 : 0;
        const isFieldBlank =  (minutesValue === 0 && hoursValue === 0) || email.length < 1;
        const textFieldClasses = `${classes.textField}${ isFieldBlank ? ` ${classes.textFieldBlank}` : ''}`;

        return (
            <React.Fragment>
                <form className={classes.root}>
                    <div className={classes.timeTrackerContainer}>
                        <TextField
                            className={textFieldClasses}
                            value={hoursValue}
                            label="Hours"
                            margin="normal"
                            variant="standard"
                            helperText="Enter from 0 to 999"
                            onChange={this.onHoursChanged}
                        />
                        <TextField
                            className={textFieldClasses}
                            value={minutesValue}
                            label="Minutes"
                            margin="normal"
                            variant="standard"
                            helperText="Enter from 0 to 999"
                            onChange={this.onMinutesChanged}
                        />
                    </div>
                    <Autocomplete
                        className={textFieldClasses}
                        disablePortal
                        options={emailList}
                        renderInput={(params) => <TextField error={isEmailInvalid} variant="standard" {...params} label="Performed by:" />}
                        inputValue={email}
                        onInputChange={this.onEmailChanged}
                    />
                    { !isFieldBlank && !isEmailInvalid ?
                        <div className={classes.buttonsContainer}>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                disabled={!!isLoading}
                                onClick={this.onPostClicked}
                            >
                                POST
                            </Button>
                            <Button
                                size="small"
                                onClick={this.onCancelClicked}
                            >
                                CANCEL
                            </Button>
                        </div>
                        : ''
                    }
                    { commentsExist ? <Divider /> : '' }
                    { comments ? 
                        <div className={classes.commentItemContainer}>
                            {
                                comments.map((item: TimeTrackComment, index: number) => {
                                    const { id } = item;
                                    return (
                                        <WorkLogItem
                                            key={id}
                                            comment={item}
                                            isLastOne = {commentsNumber === index}
                                        />
                                    );
                                })
                            }
                        </div>
                        : ''
                    }
                </form>
                <CCSpinner
                    loading={!!isLoading}
                    overlayVisible={true}
                />
            </React.Fragment>
        );
    }
}

const MUIComponent = withStyles(styles)(WorkLogEditor);
export { MUIComponent as WorkLogEditor };
